import React from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import {
  Category,
  servicesInSub,
  SubCategory,
  subCategoryBands,
} from '../../data/services';
import {
  colors,
  hairline,
  radii,
  screenPadding,
  spacing,
  useResponsive,
} from '../../theme';
import BottomSheet from '../ui/BottomSheet';
import Icon from '../ui/Icon';
import Text from '../ui/Text';
import ArtPanel from './ArtPanel';

/**
 * The step between tapping a category and seeing a list of services.
 *
 * A sheet rather than a screen: choosing a subcategory is a single decision on
 * the way somewhere, so it should rise, take one tap, and leave Home exactly
 * where it was underneath.
 *
 * Banded rather than a flat list. "Repairs" and "Installation" are different
 * intents that happen to live under one trade - someone with a leak and
 * someone fitting a new sink are not browsing the same shelf, and the band
 * headings let each skip the other's half.
 *
 * Tiles rather than rows: each subcategory carries a picture of the work,
 * which reads faster than its name does.
 */

/** Tiles per row. Three keeps a band of two or three to one line. */
const COLUMNS = 3;

/**
 * Largest the artwork is allowed to get.
 *
 * The column is ~118pt on a 411pt screen, and a picture that fills it read as
 * a poster - three of them and the second band was off the bottom of the
 * sheet. Held to 96 the tile is still clearly a photograph of the work, with
 * enough air around it that the grid reads as a menu rather than a gallery.
 */
const ART_MAX = 96;

type Props = {
  /** The tapped category, or null when the sheet is closed. */
  category: Category | null;
  onClose: () => void;
  onSelect: (sub: SubCategory) => void;
};

function SubCategorySheet({ category, onClose, onSelect }: Props) {
  const resp = useResponsive();
  const bands = category ? subCategoryBands(category.key) : [];

  /*
   * Sized in JS rather than as a percentage with an `aspectRatio` and a
   * `maxWidth`. Yoga reads `aspectRatio` both ways, so a capped width and a
   * derived height fight each other and the tile ends up neither. The column
   * is the same arithmetic the grid does, so the cap can never overflow it -
   * at 320pt the column is 88 and the art follows it down.
   *
   * Measured off the sheet's column rather than the window: on a tablet the
   * sheet is a centred 560pt panel, and a tile sized against a 1024pt screen
   * would be wider than the row it sits in.
   */
  /* `sheetWidth` on a tablet, where the sheet is a centred panel narrower
     than the page column - `contentWidth` only happened to equal it while
     both capped at 560, and the web build's wider page broke that
     coincidence: tiles sized against the page overflowed the panel. */
  const column =
    ((resp.isTablet ? resp.sheetWidth : resp.contentWidth) -
      screenPadding * 2 -
      spacing.md * (COLUMNS - 1)) /
    COLUMNS;
  const art = Math.min(ART_MAX, Math.floor(column));

  return (
    <BottomSheet visible={category !== null} onClose={onClose}>
      {/* Name only. The artwork that was here repeated the tile just tapped
          to get in, which is the one picture already fresh in mind. */}
      <View style={styles.header}>
        <View style={styles.headerText}>
          <Text variant="titleLg" numberOfLines={1}>
            {category?.label ?? ''}
          </Text>
          <Text variant="bodySm" color={colors.inkMuted}>
            Choose what you need
          </Text>
        </View>

        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Close"
          onPress={onClose}
          hitSlop={12}
          style={({ pressed }) => [styles.close, pressed ? styles.pressed : null]}
        >
          <Icon name="plus" size={18} color={colors.ink} style={styles.closeGlyph} />
        </Pressable>
      </View>

      <ScrollView
        contentContainerStyle={styles.list}
        showsVerticalScrollIndicator={false}
      >
        {bands.map(band => (
          <View key={band.title} style={styles.band}>
            <Text variant="subtitle" style={styles.bandTitle}>
              {band.title}
            </Text>

            <View style={styles.grid}>
              {band.subs.map(sub => {
                const items = servicesInSub(sub);

                return (
                  <Pressable
                    key={sub.key}
                    accessibilityRole="button"
                    accessibilityLabel={`${sub.label}. ${sub.blurb}. ${items.length} services`}
                    onPress={() => onSelect(sub)}
                    style={({ pressed }) => [
                      styles.tile,
                      pressed ? styles.pressed : null,
                    ]}
                  >
                    {/* The first service's photograph stands for the whole
                        subcategory - there is no separate artwork for one. */}
                    <ArtPanel
                      image={items[0]?.image}
                      icon={category?.icon ?? 'grid'}
                      style={[styles.tileArt, { width: art, height: art }]}
                    />
                    <Text variant="caption" align="center" numberOfLines={2}>
                      {sub.label}
                    </Text>
                  </Pressable>
                );
              })}

              {/* Pads a short band so its tiles keep the width of a full row. */}
              {Array.from(
                { length: (COLUMNS - (band.subs.length % COLUMNS)) % COLUMNS },
                (_, i) => (
                  <View key={`pad-${i}`} style={styles.tile} />
                ),
              )}
            </View>
          </View>
        ))}
      </ScrollView>
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
    paddingHorizontal: screenPadding,
    paddingTop: spacing.md,
  },
  headerText: {
    flex: 1,
    gap: 2,
  },
  close: {
    width: 30,
    height: 30,
    borderRadius: radii.pill,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceMuted,
  },
  closeGlyph: {
    transform: [{ rotate: '45deg' }],
  },

  list: {
    paddingBottom: spacing.xxl,
  },
  band: {
    paddingHorizontal: screenPadding,
    paddingTop: spacing.lg,
  },
  bandTitle: {
    marginBottom: spacing.sm,
  },
  /* No wrapping: a band holds at most `COLUMNS` tiles, and the pads below
     make every band a full row. */
  grid: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  /*
   * Equal shares of what is left after the gaps.
   *
   * Not `flexBasis: 33.3%` - three of those plus two 12pt gaps overflow the
   * row and wrap the last tile onto its own line. The padding cells carry the
   * same style, which is what stops a band of two stretching to half-width
   * tiles.
   */
  tile: {
    flex: 1,
    alignItems: 'center',
    gap: spacing.sm,
  },
  /* Square; the side comes from `art` above, which caps it at `ART_MAX` and
     otherwise lets it fill the column. */
  tileArt: {
    ...hairline,
  },
  pressed: {
    opacity: 0.6,
  },
});

export default SubCategorySheet;
